import type { ITheme } from '@xterm/xterm';
import { BRAND } from '@/brand';

/* Cascade plan 4.6 — the canvas palette.
 *
 * An xterm screen, a Guacamole desktop stream and a session recording are
 * the user's content, not app chrome. They keep one fixed, legible dark
 * palette in both app themes, so the colours live here as literals — this is
 * the one file scripts/verify/palette-guard.cjs exempts from the colour-literal
 * rule. The chrome AROUND a canvas (toolbars, status bars, forms, player
 * controls) does not use this file: it sits inside `.zta-term-chrome`
 * (src/styles/tokens-zta.css) and uses the normal d2d tokens, which that
 * class pins to their dark-theme values.
 *
 * The ANSI colours are unchanged from the palettes they replace (legibility
 * of existing output is the point). The old indigo accent is now d2d's
 * dark-theme brand violet, and the live-terminal background is d2d's dark
 * canvas, so the screen and its chrome read as one surface (Altide's canvas
 * and orange under the Altide brand).
 *
 * The macOS-style window dots the terminals used to draw were ornament and
 * are gone; the one that did something (close) is a labelled IconButton. */

/** d2d dark-theme canvas (--canvas / --d2d-bg-primary under .zta-term-chrome). */
const D2D_DARK_CANVAS = '#0a0710';
/** d2d dark-theme brand violet (--brand under .zta-term-chrome). */
const D2D_DARK_BRAND = '#9f72ff';
/** Altide dark-theme canvas (src/styles/brands/altide.css --canvas). */
const ALTIDE_DARK_CANVAS = '#0a0a0a';
/** Altide's orange (--altide-orange): the cursor is a mark, not text. */
const ALTIDE_MARK = '#ff4d00';

/* The brand is fixed at build time (src/brand.ts), so the canvas and cursor
   follow it: under Altide the screen matches the Altide chrome around it. */
const DARK_CANVAS = BRAND.id === 'altide' ? ALTIDE_DARK_CANVAS : D2D_DARK_CANVAS;
const CURSOR = BRAND.id === 'altide' ? ALTIDE_MARK : D2D_DARK_BRAND;

/** Live terminals: SSH and kubectl, full page and in-dialog. */
export const terminalTheme: ITheme = {
    background: DARK_CANVAS,
    foreground: '#c9d1d9',
    cursor: CURSOR,
    cursorAccent: DARK_CANVAS,
    selectionBackground: '#264f78',
    black: '#0d1117',
    red: '#ff7b72',
    green: '#3fb950',
    yellow: '#d29922',
    blue: '#58a6ff',
    magenta: '#bc8cff',
    cyan: '#39d2e0',
    white: '#c9d1d9',
    brightBlack: '#7d8590', // 5.36:1 on the canvas; #484f58 was 2.41 (design eval P12)
    brightRed: '#ffa198',
    brightGreen: '#56d364',
    brightYellow: '#e3b341',
    brightBlue: '#79c0ff',
    brightMagenta: '#d2a8ff',
    brightCyan: '#56d4dd',
    brightWhite: '#f0f6fc',
};

/** Terminal session replay (SessionPlayerPage) — its own palette, unchanged. */
export const replayTerminalTheme: ITheme = {
    background: '#1a1b26',
    foreground: '#a9b1d6',
    cursor: '#c0caf5',
    black: '#32344a',
    red: '#f7768e',
    green: '#9ece6a',
    yellow: '#e0af68',
    blue: '#7aa2f7',
    magenta: '#ad8ee6',
    cyan: '#449dab',
    white: '#8a8fb0', // 5.40:1; #787c99 was 4.18 (design eval P12)
};

/** Fixed backgrounds for canvas surfaces that are not an xterm instance. */
export const canvasBackground = {
    /** Behind a live xterm (matches terminalTheme.background). */
    terminal: terminalTheme.background as string,
    /** Behind a terminal replay (matches replayTerminalTheme.background). */
    replay: replayTerminalTheme.background as string,
    /** Behind a desktop stream, desktop recording video or canvas replay. */
    desktop: '#000000',
} as const;

/** The plain-text live-session transcript (SessionJoinPage): phosphor on black. */
export const transcriptTheme = {
    background: '#000000',
    foreground: '#4ade80',
} as const;

/** A Guacamole `cfill` / `cstroke` colour: 0–255 channels, alpha 0–255.
 *  Protocol data painted onto the remote-desktop canvas. */
export function guacColor(r: number, g: number, b: number, a: number): string {
    return `rgba(${r},${g},${b},${a / 255})`;
}
